import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { misService } from '../services/misService'

interface HistorySummaryItem {
  periodStartDate: string
  periodEndDate: string
  status: string
  clientName: string
  recordCount: number
  totalFinalPayout?: number
}

const MISHistorySummary: React.FC = () => {
  const navigate = useNavigate()
  const [data, setData] = useState<HistorySummaryItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fromDate, setFromDate] = useState<Date | null>(null)
  const [toDate, setToDate] = useState<Date | null>(null)

  // Format date to YYYY-MM-DD
  const formatYMD = (d: Date) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${dd}`
  }

  // Load data
  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      const params: any = {}
      if (fromDate) params.fromDate = formatYMD(fromDate)
      if (toDate) params.toDate = formatYMD(toDate)

      const res = await misService.getMISHistorySummary(params)
      setData(res.data || [])
    } catch (err: any) {
      console.error('Failed to load history summary', err)
      setError(err?.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  // Get status color
  const getStatusColor = (status: string) => {
    switch ((status || '').toUpperCase()) {
      case 'PENDING':
        return { bg: '#fff3e0', text: '#e65100', chip: 'warning' }
      case 'PAID':
        return { bg: '#e8f5e9', text: '#1b5e20', chip: 'success' }
      case 'DEDUCTED':
        return { bg: '#ffebee', text: '#b71c1c', chip: 'error' }
      case 'HOLD':
        return { bg: '#e3f2fd', text: '#0d47a1', chip: 'info' }
      default:
        return { bg: '#f5f5f5', text: '#424242', chip: 'default' }
    }
  }

  // Only show non-DEDUCTED statuses
  const filteredData = data.filter(
    (item) => item.status?.toString().toUpperCase() !== 'DEDUCTED',
  )

  const statusCounts = filteredData.reduce((acc, item) => {
    const status = item.status || 'UNKNOWN'
    acc[status] = (acc[status] || 0) + item.recordCount
    return acc
  }, {} as Record<string, number>)

  const totalRecords = filteredData.reduce((sum, item) => sum + item.recordCount, 0)
  const totalFinalPayout = filteredData.reduce(
    (sum, item) => sum + (item.totalFinalPayout ?? 0),
    0,
  )

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/mis')}
            variant="outlined"
          >
            Back
          </Button>
          <div>
            <Typography variant="h4">MIS History Summary</Typography>
            <Typography variant="body2" color="textSecondary">
              View MIS status distribution across date ranges
            </Typography>
          </div>
        </Box>
      </Box>

      {/* Date Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={(d) => setFromDate(d)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={(d) => setToDate(d)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button variant="contained" color="primary" onClick={loadData} fullWidth>
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#ffebee', borderLeft: '4px solid #d32f2f' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Status Summary Cards */}
          <Grid container spacing={2} mb={3}>
            {Object.entries(statusCounts).map(([status, count]) => {
              const colors = getStatusColor(status)
              const percentage = totalRecords > 0 ? (count / totalRecords) * 100 : 0
              return (
                <Grid item xs={12} sm={6} md={3} key={status}>
                  <Card sx={{ bgcolor: colors.bg, border: `2px solid ${colors.text}` }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {status}
                        </Typography>
                        <Chip label={count} color={colors.chip as any} size="small" />
                      </Box>
                      <Typography variant="h5" sx={{ color: colors.text, fontWeight: 'bold' }}>
                        {count}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {percentage.toFixed(1)}% of total
                      </Typography>
                      <Box mt={1}>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{
                            bgcolor: 'rgba(0,0,0,0.1)',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: colors.text,
                            },
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>

          {/* Total Summary */}
          {/* <Paper sx={{ p: 2.5, mb: 3, bgcolor: '#f5f5f5' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Records
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                    {totalRecords}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Final
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                    {`₹${Number(totalFinalPayout).toLocaleString('en-IN', {
                      maximumFractionDigits: 0,
                    })}`}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Date Range
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {fromDate ? fromDate.toLocaleDateString() : 'All'} -{' '}
                    {toDate ? toDate.toLocaleDateString() : 'All'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper> */}

          {/* Detailed Table */}
          {filteredData.length > 0 ? (
            <Paper>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Period Start</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Period End</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Client Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Count
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Total Final
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.map((item, idx) => {
                      const colors = getStatusColor(item.status)
                      return (
                        <TableRow
                          key={idx}
                          sx={{
                            '&:hover': { bgcolor: 'action.hover' },
                            borderLeft: `4px solid ${colors.text}`,
                          }}
                        >
                          <TableCell>
                            {new Date(item.periodStartDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {new Date(item.periodEndDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: '500' }}>
                              {item.clientName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.status}
                              color={colors.chip as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontWeight: 'bold' }}>
                              {item.recordCount}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {item.totalFinalPayout
                              ? `₹${Number(item.totalFinalPayout).toLocaleString('en-IN', {
                                  maximumFractionDigits: 0,
                                })}`
                              : '-'}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">No data available for the selected date range</Typography>
            </Paper>
          )}
        </>
      )}
    </Box>
  )
}

export default MISHistorySummary
